import { clientAppFetch } from './clientAppFetch';
import { clientIdentityFields } from './clientIdentity';
import { PACK_DEFINITIONS } from './packSystem';

const DAY_MS = 86_400_000;

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function validIso(value) {
  return typeof value === 'string' && Number.isFinite(Date.parse(value));
}

function addCalendarMonth(iso) {
  const date = new Date(iso);
  const day = date.getUTCDate();
  date.setUTCDate(1);
  date.setUTCMonth(date.getUTCMonth() + 1);
  const max = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + 1, 0)).getUTCDate();
  date.setUTCDate(Math.min(day, max));
  return date.toISOString();
}

export function buildPackAccessGrant(packType, startsAt, sourceRef) {
  if (!validIso(startsAt) || typeof sourceRef !== 'string' || !sourceRef.trim()) {
    throw new Error('Base du contrat d’accès invalide');
  }
  const pack = PACK_DEFINITIONS[packType];
  if (!pack || pack.archived === true) return null;
  let sourceKind;
  let accessLevel;
  let endsAt;
  if (packType === 'consultation_initiale_220') {
    sourceKind = 'consultation_220';
    accessLevel = 'self_service';
    endsAt = addCalendarMonth(startsAt);
  } else if (packType === 'suivi_3m_990' || packType === 'suivi_6m_1990') {
    sourceKind = packType === 'suivi_3m_990' ? 'pack_3m' : 'pack_6m';
    accessLevel = 'followup';
    if (!Number.isInteger(pack.durationWeeks) || pack.durationWeeks <= 0) {
      throw new Error('Durée du pack absente');
    }
    endsAt = new Date(Date.parse(startsAt) + pack.durationWeeks * 7 * DAY_MS).toISOString();
  } else {
    return null;
  }
  return {
    source_kind: sourceKind,
    source_ref: sourceRef.trim(),
    access_level: accessLevel,
    starts_at: new Date(startsAt).toISOString(),
    ends_at: endsAt,
    grace_until: new Date(Date.parse(endsAt) + 4 * DAY_MS).toISOString(),
    reason: packType === 'consultation_initiale_220'
      ? 'Mois d’application autonome inclus avec la consultation initiale'
      : `Accès de suivi inclus avec ${pack.label}`,
  };
}

export function buildManualMonthGrant(startsAt = new Date().toISOString(), sourceRef = crypto.randomUUID()) {
  if (!validIso(startsAt)) throw new Error('Date du mois offert invalide');
  const endsAt = addCalendarMonth(startsAt);
  return {
    source_kind: 'manual_month',
    source_ref: `manual:${sourceRef}`,
    access_level: 'self_service',
    starts_at: new Date(startsAt).toISOString(),
    ends_at: endsAt,
    grace_until: new Date(Date.parse(endsAt) + 4 * DAY_MS).toISOString(),
    reason: 'Mois d’application autonome offert par Anissa',
  };
}

export function buildLegacyPackAccessTimeline(client, access, now = Date.now()) {
  const startedAt = client?.packStartedAt || client?.pack_started_at;
  const confirmed = client?.packStartedAtConfirmed ?? client?.pack_started_at_confirmed;
  if (access?.state !== 'legacy' || confirmed !== true || !validIso(startedAt)) return null;
  const grant = buildPackAccessGrant(
    client?.packType || client?.pack_type,
    startedAt,
    `legacy:${new Date(startedAt).toISOString()}`,
  );
  if (!grant) return null;
  const endsAt = Date.parse(grant.ends_at);
  const graceUntil = Date.parse(grant.grace_until);
  const state = now <= endsAt ? 'active' : now <= graceUntil ? 'grace' : 'expired';
  return buildAccessTimeline({
    level: grant.access_level,
    state,
    starts_at: grant.starts_at,
    ends_at: grant.ends_at,
    grace_until: grant.grace_until,
  }, now);
}

export function buildAccessTimeline(access, now = Date.now()) {
  if (!access || typeof access !== 'object' || !Number.isFinite(now)) return null;
  const startsAt = Date.parse(access.starts_at);
  const endsAt = Date.parse(access.ends_at);
  const graceUntil = Date.parse(access.grace_until);
  const levelLabel = access.level === 'followup' ? 'suivi' : 'autonomie';

  let segmentStart;
  let segmentEnd;
  let startLabel;
  let endLabel;
  let summary;
  let afterLabel = null;
  let afterAt = null;

  if (access.state === 'active' && Number.isFinite(startsAt) && Number.isFinite(endsAt) && startsAt < endsAt) {
    segmentStart = startsAt;
    segmentEnd = endsAt;
    startLabel = `Début du ${levelLabel}`;
    endLabel = access.level === 'followup' ? 'Fin du suivi inclus' : 'Fin de l’autonomie';
    const days = Math.max(0, Math.ceil((endsAt - now) / DAY_MS));
    summary = days === 0 ? 'Dernier jour inclus' : `${days} jour${days > 1 ? 's' : ''} restant${days > 1 ? 's' : ''}`;
    if (Number.isFinite(graceUntil) && endsAt < graceUntil) {
      afterLabel = 'Puis 4 jours de grâce';
      afterAt = new Date(graceUntil).toISOString();
    }
  } else if (access.state === 'grace' && Number.isFinite(endsAt) && Number.isFinite(graceUntil) && endsAt < graceUntil) {
    segmentStart = endsAt;
    segmentEnd = graceUntil;
    startLabel = 'Fin de la période incluse';
    endLabel = 'Fin de la grâce';
    const days = Math.max(0, Math.ceil((graceUntil - now) / DAY_MS));
    summary = days === 0 ? 'Dernier jour de grâce' : `${days} jour${days > 1 ? 's' : ''} de grâce restant${days > 1 ? 's' : ''}`;
    afterLabel = 'L’accès au contenu sera ensuite verrouillé.';
  } else if (access.state === 'expired' && Number.isFinite(startsAt)) {
    const finalAt = Number.isFinite(graceUntil) && graceUntil > startsAt
      ? graceUntil
      : endsAt;
    if (!Number.isFinite(finalAt) || finalAt <= startsAt) return null;
    segmentStart = startsAt;
    segmentEnd = finalAt;
    startLabel = 'Début de l’accès';
    endLabel = 'Accès terminé';
    summary = 'Période terminée';
  } else {
    return null;
  }

  const progress = access.state === 'expired'
    ? 100
    : clamp(((now - segmentStart) / (segmentEnd - segmentStart)) * 100, 0, 100);

  return {
    state: access.state,
    progress,
    segment_start: new Date(segmentStart).toISOString(),
    segment_end: new Date(segmentEnd).toISOString(),
    start_label: startLabel,
    end_label: endLabel,
    summary,
    after_label: afterLabel,
    after_at: afterAt,
  };
}

export function fetchClientAccess(client) {
  return clientAppFetch('/api/admin/client-access', {
    method: 'GET',
    query: clientIdentityFields(client),
  });
}

export async function grantClientAccess(client, grant) {
  const identity = clientIdentityFields(client);
  if (!identity.email && !identity.client_id) throw new Error('Identité app cliente absente');
  const result = await clientAppFetch('/api/admin/client-access', {
    method: 'POST',
    payload: { ...identity, ...grant },
  });
  if (result?.ok !== true || result?.access?.version !== 1) {
    throw new Error('L’accès cliente n’a pas été confirmé');
  }
  return result;
}

export async function grantPackClientAccess(client, { startsAt, sourceRef }) {
  const packType = client?.packType || client?.pack_type;
  const grant = buildPackAccessGrant(packType, startsAt, sourceRef);
  if (!grant) return { ok: true, skipped: true };
  return grantClientAccess(client, grant);
}
